import { useContext, useMemo } from 'react';
import { usePricesQuery } from '@/api/prices/prices';
import { AccessoryLineType } from '@/components/features/AppSolutionBuilder/shared/AccessoryLine';
import { ProductCardProps } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { PriceResponse } from '@/generated/data.schemas';

/**
 * Extracts product item numbers from an array of products.
 * Handles both ProductCardProps (nested itemNo) and AccessoryLineType (direct itemNo).
 * @param products - Array of products to extract item numbers from
 * @returns Array of product item numbers (strings)
 */
export function mapProductIdsToPrices<T extends ProductCardProps | AccessoryLineType>(
	products: readonly T[],
): string[] {
	const result: string[] = [];
	for (let i = 0; i < products.length; i++) {
		const p = products[i];
		const itemNo = 'product' in p ? p.product?.itemNo : (p as AccessoryLineType).itemNo;
		if (itemNo) result.push(itemNo);
	}
	return result;
}

/**
 * Maps price data to products by matching productNumber to itemNo.
 * Returns products with prices included, filtering out invalid prices (< 0).
 *
 * @param products - Array of products to map prices to (ProductCardProps or AccessoryLineType)
 * @param prices - Array of price responses from the API
 * @returns Products with mapped prices or original products if no prices available
 */
export function mapProductPrices<T extends ProductCardProps | AccessoryLineType>(
	products: T[],
	prices: PriceResponse[] | undefined,
): T[] {
	if (!prices || prices.length === 0) return products;

	const priceMap = new Map(prices.map((p) => [p.productNumber, p.price]));
	const result: T[] = new Array(products.length);

	for (let i = 0; i < products.length; i++) {
		const product = products[i];
		const itemNo = 'product' in product ? product.product?.itemNo : (product as AccessoryLineType).itemNo;
		const price = itemNo ? priceMap.get(itemNo) : undefined;

		if ('product' in product) {
			result[i] = {
				...product,
				product: product.product ? { ...product.product, price } : undefined,
			} as T;
		} else {
			result[i] = {
				...product,
				price,
			} as T;
		}
	}

	return result;
}

/* fetch DIH prices for ECS products */
export const useProductPrices = <T extends ProductCardProps | AccessoryLineType>(products: T[], isLoading: boolean) => {
	const { segmentationId } = useContext(SegmentationContext);

	const productNumbers = useMemo(() => {
		if (isLoading) return [];
		return mapProductIdsToPrices(products);
	}, [isLoading, products]);

	const {
		data: prices,
		isLoading: isPricesLoading,
		isError: isPricesError,
	} = usePricesQuery(segmentationId, productNumbers, productNumbers.length > 0);

	const productsWithPrices = useMemo(() => {
		return mapProductPrices(products, prices ?? undefined);
	}, [products, prices]);

	return { productsWithPrices, isPricesLoading, isPricesError };
};
